import * as R from 'ramda'
import type { Constant, Nil } from '@pbt/pbt-ui-components'
import { DateUtils, LanguageUtils, moment } from '@pbt/pbt-ui-components'

import { RxPrescriptionOrigin, RxType } from '~/api/graphql/generated/types'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'
import type { OrderDisplayStatus } from '~/constants/orderStatusChip'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import type { PrescriptionTimelineItem, RxCardItem } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import { getOrderDisplayStatus } from '~/utils/orderStatusChipUtils'
import {
  getPrescriptionType,
  getPrescriptionTypeLabel,
} from '~/utils/prescription'
import { getRemainingRefillsCount } from '~/utils/prescriptionUtils'

import type { OrderTableRow } from './components/OrderTable'

const EMPTY_PLACEHOLDER = '--'
const REFILL_SEPARATOR = ', '

const toTrimmedString = (value: string | Nil): string =>
  value == null ? '' : String(value).trim()

export const formatValueOrPlaceholder = (value: string | Nil): string => {
  const s = toTrimmedString(value)
  return s || EMPTY_PLACEHOLDER
}

type NullableType<T> = T | Nil
type NullableString = NullableType<string>
type NullablePrescriptionType = NullableType<PrescriptionType>

export type DisplayField = {
  boldValue?: boolean
  label: string
  onClick?: () => void
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  tooltipText?: string
  value: string
}

export interface PrescriptionFlags {
  isCanceled: boolean
  isChewy: boolean
  isDeclined: boolean
  isPendingVetApproval: boolean
  isScriptOut: boolean
  stateName: string | null
}

export interface GetRxFieldsOptions {
  onDispenseRefill?: () => void
  prescriptionProductSizeUnit?: Constant[]
  prescriptionStates?: Constant[]
  productSizeUnit?: Constant[]
}

type OrderTableFieldRowOptions = Pick<
  GetRxFieldsOptions,
  'prescriptionProductSizeUnit' | 'productSizeUnit' | 'prescriptionStates'
>

export const getPrescriptionFlags = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  prescriptionStates?: Constant[],
): PrescriptionFlags => {
  const stateName =
    rx.stateId && prescriptionStates
      ? (prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null)
      : null

  const isDeclined = stateName === PrescriptionStates.DECLINED
  const isCanceled = stateName === PrescriptionStates.CANCELED
  const isPendingVetApproval =
    stateName === PrescriptionStates.PENDING_VET_APPROVAL

  return {
    stateName,
    isDeclined,
    isCanceled,
    isPendingVetApproval,
    isScriptOut: prescriptionType === PrescriptionType.SCRIPT_OUT,
    isChewy: prescriptionType === PrescriptionType.CHEWY,
  }
}

/**
 * Determines if fields should be suppressed (use custom/declined view)
 * for prescriptions that are declined or pending vet approval (but not canceled).
 */
export const shouldSuppressFields = (flags: PrescriptionFlags): boolean =>
  (flags.isDeclined || flags.isPendingVetApproval) && !flags.isCanceled

const DateFormatters = {
  date: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY'),
  dateTime: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY | h:mmA'),
}

const formatDateOrPlaceholder = (
  value: NullableString,
  format: (v: NullableString) => string,
): string => {
  const s = toTrimmedString(value)
  return s ? format(value) : formatValueOrPlaceholder(value)
}

export const computeRelativeTime = (
  date: NullableString,
  timezone: string,
): string | null => {
  if (!date) return null
  const m = moment.tz(date, timezone)
  return m.isValid() ? m.fromNow() : null
}

const isPrescriptionFilled = (rx: Prescription): boolean =>
  Boolean(rx.filledBy ?? rx.filledById)

export const getPrescriptionMostRecentDate = (
  rx: Prescription,
): string | null =>
  rx.modificationDate ??
  rx.updatedAt ??
  rx.creationDate ??
  (rx as { createdAt?: string }).createdAt ??
  null

const PrescriptionFields = {
  expirationDate: (rx: Prescription): NullableString => {
    const drugExpirationDate =
      'drugExpirationDate' in rx
        ? (rx as { drugExpirationDate?: string }).drugExpirationDate
        : undefined
    return (
      rx.quantityDrugExpirationDate ??
      drugExpirationDate ??
      rx.refillExpirationDate ??
      null
    )
  },

  lastFillDate: (rx: Prescription, isInHouse = false): string | null => {
    const all = [rx, ...(rx.refills ?? [])]
    const dates = all
      .filter((p) => isInHouse || isPrescriptionFilled(p))
      .map(getPrescriptionMostRecentDate)
      .filter((date): date is string => date !== null)

    return dates.length > 0
      ? dates.reduce((a, b) => (a > b ? a : b), dates[0])
      : null
  },

  numberOfRefills: (rx: Prescription): number | null =>
    rx.quantityNumbRefills ?? null,

  refillsRemaining: (rx: Prescription): number => getRemainingRefillsCount(rx),
}

const toStringOrZero = (value: number | null | undefined): string =>
  value === null || value === undefined ? '0' : String(value)

interface QuantityRequestedOptions {
  prescriptionProductSizeUnit: Constant[]
}

const getQuantityRequested = (
  rx: Prescription,
  { prescriptionProductSizeUnit }: QuantityRequestedOptions,
): string => {
  const quantity = rx.totalQuantity ?? getHighValue(rx.quantity) ?? 0

  const unitOfProduct = prescriptionProductSizeUnit.find(
    (unit) => unit.id === rx.totalQuantityUnitId,
  )

  const quantityText = unitOfProduct?.name
    ? `${quantity} ${unitOfProduct.name}`
    : String(quantity)

  const { quantityNumbRefills } = rx
  const refillText =
    quantityNumbRefills && quantityNumbRefills > 0
      ? `${REFILL_SEPARATOR}${i18n.t(
          'Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS',
          {
            number: quantityNumbRefills,
          },
        )}`
      : `${REFILL_SEPARATOR}${i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`

  return `${quantityText}${refillText}`
}

const getFulfillmentText = (prescriptionType: PrescriptionType): string => {
  switch (prescriptionType) {
    case PrescriptionType.CHEWY:
      return i18n.t('Common:CHEWY_COM')
    case PrescriptionType.IN_HOUSE:
      return i18n.t('Common:IN_HOUSE')
    case PrescriptionType.SCRIPT_OUT:
      return i18n.t('Common:OUTSIDE_PHARMACY')
    default:
      return ''
  }
}

const getLastFill = (rx: Prescription, isInHouse = false): string => {
  const lastFillDate = PrescriptionFields.lastFillDate(rx, isInHouse)
  return DateFormatters.date(lastFillDate)
}

const buildFields = (
  conditionalFields: Array<DisplayField | null | false>,
): DisplayField[] => conditionalFields.filter(Boolean) as DisplayField[]

const getOrderDate = (rx: Prescription): NullableString =>
  rx.creationDate ?? (rx as { createdAt?: string }).createdAt ?? null

const getDeclinedFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts: GetRxFieldsOptions,
): DisplayField[] => {
  const { prescriptionProductSizeUnit = [] } = opts

  const orderDate = getOrderDate(rx)
  return buildFields([
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
      value: formatDateOrPlaceholder(orderDate, DateFormatters.dateTime),
    },

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    prescriptionProductSizeUnit.length > 0
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
          value: getQuantityRequested(rx, { prescriptionProductSizeUnit }),
        }
      : null,
  ])
}

const getExpirationField = (rx: Prescription): DisplayField | null => {
  const expirationDate = PrescriptionFields.expirationDate(rx)
  return {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.EXPIRATION'),
    value: formatDateOrPlaceholder(expirationDate, DateFormatters.date),
  }
}

const getLastFillField = (
  rx: Prescription,
  prescriptionType: PrescriptionType,
): DisplayField | null => {
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const lastFill = getLastFill(rx, isInHouse)

  return {
    boldValue: !lastFill,
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.LAST_FILL'),
    value:
      lastFill ||
      (isInHouse
        ? formatValueOrPlaceholder(null)
        : i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_PROGRESS')),
  }
}

const getStandardFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts?: GetRxFieldsOptions,
): DisplayField[] => {
  const isScriptOut = prescriptionType === PrescriptionType.SCRIPT_OUT

  return buildFields([
    getExpirationField(rx),

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    {
      label: i18n.t(
        'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
      ),
      value: toStringOrZero(PrescriptionFields.numberOfRefills(rx)),
    },

    !isScriptOut && {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.REFILLS_REMAINING'),
      value: toStringOrZero(PrescriptionFields.refillsRemaining(rx)),
      onClick:
        opts?.onDispenseRefill && PrescriptionFields.refillsRemaining(rx) > 0
          ? opts.onDispenseRefill
          : undefined,
      tooltipText:
        opts?.onDispenseRefill && PrescriptionFields.refillsRemaining(rx) > 0
          ? i18n.t('Common:DISPENSE_REFILL')
          : undefined,
    },

    !isScriptOut && prescriptionType
      ? getLastFillField(rx, prescriptionType)
      : null,
  ])
}

export const getRxFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetRxFieldsOptions = {},
): DisplayField[] => {
  const { prescriptionStates } = options

  const flags = getPrescriptionFlags(rx, prescriptionType, prescriptionStates)
  const shouldShowDeclinedFields = shouldSuppressFields(flags)

  return shouldShowDeclinedFields
    ? getDeclinedFields(rx, prescriptionType, options)
    : getStandardFields(rx, prescriptionType, options)
}

export const getInstructionsField = (rx: Prescription): DisplayField => ({
  label: i18n.t('Common:INSTRUCTION_PRINTED_ON_LABEL'),
  value: formatValueOrPlaceholder(rx.notes),
})

export const getPrescriptionStateName = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => getPrescriptionFlags(rx, null, prescriptionStates).stateName

/**
 * Checks if a prescription is a vet diet prescription.
 */
export const isVetDietPrescription = (prescription: Prescription): boolean =>
  prescription.rxType === RxType.VetDiet

/**
 * Checks if a prescription was dispensed from inventory.
 * Returns true for inventory orders or in-house prescriptions not from vet origin.
 */
export const isDispensedFromInventory = (prescription: {
  origin?: string | null | RxPrescriptionOrigin
  prescriptionType?: string | PrescriptionType
  type?: string
}): boolean => {
  const prescriptionTypeValue = getPrescriptionType(
    prescription.prescriptionType as PrescriptionType | undefined,
  )
  return (
    prescription.type === OrderType.INVENTORY ||
    (prescriptionTypeValue.isInHouse &&
      prescription.origin !== RxPrescriptionOrigin.Vet)
  )
}

/**
 * Checks if a field label matches the QTY_REQUESTED label.
 */
export const isQtyRequestedField = (
  fieldLabel: string,
  qtyRequestedLabel: string,
): boolean => fieldLabel === qtyRequestedLabel

export const getOrderFillStatusText = (
  rx: Prescription,
  prescriptionType?: PrescriptionType,
): string => {
  const fillDate = getPrescriptionMostRecentDate(rx)

  if (
    isDispensedFromInventory({
      ...rx,
      prescriptionType: prescriptionType ?? rx.prescriptionType,
    })
  ) {
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_FILLED', {
      date: DateFormatters.date(fillDate),
    })
  }

  return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_COMPLETED', {
    date: DateFormatters.date(fillDate),
  })
}

const formatOrderTableQuantity = (
  rx: Prescription,
  parentPrescription: Prescription | undefined,
  opts: OrderTableFieldRowOptions,
): string => {
  const { prescriptionProductSizeUnit = [] } = opts
  const quantity =
    rx.totalQuantity ??
    parentPrescription?.totalQuantity ??
    getHighValue(rx.quantity) ??
    getHighValue(parentPrescription?.quantity) ??
    0
  const unitId =
    rx.totalQuantityUnitId ??
    parentPrescription?.totalQuantityUnitId ??
    undefined
  const unitName = unitId
    ? prescriptionProductSizeUnit.find((u) => u.id === unitId)?.name
    : undefined

  return unitName ? `${quantity} ${unitName}` : String(quantity)
}

interface GetOrderTableRowOptions extends OrderTableFieldRowOptions {
  fillIndex: number
  isInHouse: boolean
  parentPrescription: Prescription | undefined
  prescriptionType: NullablePrescriptionType
  timezone: string
  totalFills: number
}

const getOrderTableRow = (
  rx: Prescription,
  options: GetOrderTableRowOptions,
): OrderTableRow[] => {
  const {
    fillIndex,
    parentPrescription,
    prescriptionType,
    timezone,
    totalFills,
    ...opts
  } = options
  const orderDate = getOrderDate(rx)
  const expirationDate = PrescriptionFields.expirationDate(rx)
  const fillStatus = getOrderFillStatusText(rx, prescriptionType ?? undefined)

  return [
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
      value: formatDateOrPlaceholder(orderDate, DateFormatters.dateTime),
      prescription: rx,
      parentPrescription,
      relativeTime: computeRelativeTime(orderDate, timezone),
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
      value: formatOrderTableQuantity(rx, parentPrescription, opts),
      prescription: rx,
      parentPrescription,
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_NUMBER'),
      value: `${fillIndex} / ${totalFills}`,
    },
    {
      label: i18n.t('Common:DRUG_EXPIRATION'),
      value: formatDateOrPlaceholder(expirationDate, DateFormatters.date),
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS'),
      value: fillStatus,
    },
  ]
}

const getCardHeaderForPrescription = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
): string | null => {
  if (rx.type === OrderType.INVENTORY) {
    return i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  if (prescriptionType === PrescriptionType.CHEWY) {
    return i18n.t('Common:CHEWY_COM')
  }

  if (prescriptionType === PrescriptionType.IN_HOUSE) {
    return rx.origin === RxPrescriptionOrigin.Vet
      ? i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
      : i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  return null
}

export const getOrderTableSubHeader = getCardHeaderForPrescription

/** Returns the prescription from a row, optionally filtered by refill status. */
export const getPrescriptionFromRow = <
  T extends { parentPrescription?: Prescription; prescription?: Prescription },
>(
  row: T[],
  isRefill?: boolean,
): Prescription | undefined => {
  const prescription = row.find((cell) => cell.prescription)?.prescription

  if (isRefill === undefined) return prescription

  const hasParent = row.some((cell) => cell.parentPrescription)
  const shouldReturnPrescription = hasParent === isRefill

  return shouldReturnPrescription ? prescription : undefined
}

export interface OrderTableContent {
  cardHeaders: Array<{
    status: OrderDisplayStatus | null
    text: string | null
  }>
  headers: string[]
  rows: OrderTableRow[][]
}

interface GetOrderTableFieldsOptions extends OrderTableFieldRowOptions {
  timezone: string
}

export const getOrderTableFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetOrderTableFieldsOptions,
): OrderTableContent | null => {
  const isOrderWorkflow = rx.type === OrderType.INVENTORY
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE

  if (!isOrderWorkflow && !isChewy && !isInHouse) return null

  const { timezone, ...opts } = options
  const refills = rx.refills ?? []
  const totalFills = refills.length + 1

  const baseRowOptions: Omit<
    GetOrderTableRowOptions,
    'fillIndex' | 'parentPrescription'
  > = {
    ...opts,
    isInHouse,
    prescriptionType,
    timezone,
    totalFills,
  }

  const firstRow = getOrderTableRow(rx, {
    ...baseRowOptions,
    fillIndex: 1,
    parentPrescription: undefined,
  })

  const allPrescriptions = [...refills].reverse().concat(rx)

  const rows: OrderTableRow[][] = allPrescriptions.map(
    (prescription, index) => {
      const isParent = prescription.id === rx.id
      const fillIndex = totalFills - index
      return getOrderTableRow(prescription, {
        ...baseRowOptions,
        fillIndex,
        parentPrescription: isParent ? undefined : rx,
      })
    },
  )

  const headers = firstRow.map((field) => field.label)
  const parentHeader = getCardHeaderForPrescription(rx, prescriptionType)

  const cardHeaders = allPrescriptions.map((prescription) => {
    const status = getOrderDisplayStatus({
      prescription,
      prescriptionStates: opts.prescriptionStates,
    })

    const isParent = prescription.id === rx.id
    if (isParent) {
      return {
        text: parentHeader ?? i18n.t('Common:ORDER'),
        status,
      }
    }

    const refillHeader = getCardHeaderForPrescription(
      prescription,
      prescription.prescriptionType,
    )

    const dispensedFromInventory = i18n.t('Common:DISPENSED_FROM_INVENTORY')
    const text =
      refillHeader === dispensedFromInventory
        ? i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.REFILL_DISPENSED')
        : (refillHeader ?? parentHeader ?? i18n.t('Common:ORDER'))

    return {
      text,
      status,
    }
  })

  return {
    cardHeaders,
    headers,
    rows,
  }
}

export const getPrescriptionTitle = (
  prescription: Prescription,
  item?: RxCardItem,
  includeType?: boolean,
): string => {
  const baseTitle =
    item?.prescription?.name ??
    item?.prescription?.masterName ??
    LanguageUtils.getTranslatedFieldName(prescription)

  if (includeType) {
    const prescriptionTypeLabel = getPrescriptionTypeLabel({
      prescriptionType: prescription.prescriptionType,
      origin: prescription.origin,
    })
    return `${prescriptionTypeLabel}–${baseTitle}`
  }

  return baseTitle
}

/**
 * Gets the prescription timeline item with origin data merged into prescription.
 */
export const getPrescriptionWithOrigin = (
  item: PrescriptionTimelineItem,
): PrescriptionTimelineItem => ({
  ...item,
  prescription: {
    ...item.prescription,
    origin: item.origin,
  },
})

/**
 * Prepares a prescription for the refill dialog by omitting specified fields.
 * Prevents duplicate charges and incorrect data from being copied to refills.
 *
 * @param prescription - The prescription to prepare
 * @param fieldsToOmit - Prescription keys to omit (e.g., 'id', 'soapId'). 'retailOrderLineItem' is always omitted automatically.
 * @returns An object with cleaned prescription and parent for the refill dialog
 */
export const prepareRefillPrescription = (
  prescription: Prescription,
  fieldsToOmit: (keyof Prescription)[],
): { parent: Prescription; prescription: Prescription } => {
  // Always omit retailOrderLineItem to prevent duplicate charges
  const fieldsToOmitSet = new Set([...fieldsToOmit, 'retailOrderLineItem'])
  const prescriptionFieldsToOmit = Array.from(fieldsToOmitSet)
  // Parent doesn't need 'id' omitted
  const parentFieldsToOmit = prescriptionFieldsToOmit.filter((f) => f !== 'id')

  return {
    prescription: R.omit(
      prescriptionFieldsToOmit,
      prescription,
    ) as Prescription,
    parent: R.omit(parentFieldsToOmit, prescription) as Prescription,
  }
}
